import React, { useEffect, useState, useRef } from "react";
import {
  bp_add_meta_data,
  bp_get_machine_by_machine_typ,
  bp_machine_data,
} from "../../../utils/api";
import { toast } from "react-toastify";
import { Form, Input, Select, Button, Descriptions, Avatar } from "antd";
import MainCard from '@/components/MainCard';
import { useDispatch, useSelector } from "react-redux";
import { settab_number } from '@/store/reducers/tabNumber';
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { defaultColors } from '@/themes/antd-theme';
import { SopChip } from '@/components/SopLinks';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import AcknowledgeHandoverModal from '@/components/AcknowledgeHandoverModal';
import { ackApi, isHandoverAckRequired } from '@/utils/ackApi';

function AddData() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const operation_data = state?.operation_data;
  const dispatch = useDispatch();
  const [machine, set_machine] = useState("");
  const [param_value, set_param_value] = useState("");
  const [remark, set_remark] = useState("");
  const [handoverOp, setHandoverOp] = useState(null);   // 428 HANDOVER_ACK_REQUIRED → read & e-sign this operation's handover note, then retry

  const handleSubmit = (e) => {
    e.preventDefault();
    submit_function();
  };

  //####################### Handle Api's######################################################

  const [machine_data, setMachineData] = useState([]);
  const get_all_machine = async () => {
    try {
      await bp_get_machine_by_machine_typ(success_machine_list_get, {
        machine_type_id: operation_data?.machine_id,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_machine_list_get = (res) => {
    setMachineData(res?.data?.data);
  };

  useEffect(() => {
    get_all_machine();
  }, [operation_data]);

  const submit_function = async (metaData) => {
    try {
      // lane K: axios call so a 428 ACK_REQUIRED body reaches us (fetch helper swallows non-2xx); same payload as bp_save_batch_parameters
      const res = await ackApi.saveBatchParameters({
        process_parameter: param_value?.filter(Boolean),
        operation_id: operation_data?.operation_id,
        machine_id: machine,
        batch_number: operation_data?.batch_number,
      });
      if (isHandoverAckRequired(res)) {
        setHandoverOp(res.operation_id || operation_data?.operation_id);
        return;
      }
      if (res?.httpStatus && res.httpStatus >= 400 && res.status !== "success") {
        toast.error(res?.message || `Request failed (${res.httpStatus})`);
        return;
      }
      success_bp_save_batch_parameters(res);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_bp_save_batch_parameters = (res) => {
    if (res.status === "failed") {
      toast.warning(res.message);
    } else {
      navigate("/batch");
      resetFunction();
      const correction = (res?.data?.ipc_events || []).find(
        (e) => e?.action === "CORRECT"
      );
      if (correction) {
        // Q2 correction loop: the reading is recorded as an attempt, no hold / ticket raised.
        toast.warning(
          `Out of limit (${correction.limit_text}) — adjust and re-measure. ` +
            `Attempt ${correction.attempt} of ${correction.max_attempts}. No hold raised.` +
            (correction.instruction ? ` ${correction.instruction}` : ""),
          { autoClose: 8000 }
        );
      } else if (res?.data?.auto_hold_triggered) {
        toast.error(
          res.message ||
            "Parameter out of limit — operation placed on hold and a deviation raised."
        );
      } else {
        toast.success("Parameter value added successfully");
      }
    }
  };

  const resetFunction = () => {
    set_param_value([]);
    set_remark("");
    set_machine("");
  };

  const handle_parameter = (ingredient_id, e, index) => {
    if (e.target.value === "") {
      let temp = [...param_value];
      temp.splice(index, 1);
      set_param_value(temp);
    } else {
      let temp = [...param_value];
      temp[index] = {
        process_parameter_id: ingredient_id,
        value: e.target.value,
      };
      set_param_value(temp);
    }
  };

  return (
    <MainCard darkTitle title="Parameters" backbutton>
      <form onSubmit={handleSubmit}>
        <Descriptions
          title=""
          bordered
          column={2}
          contentStyle={{
            fontSize: "24px",
          }}
          labelStyle={{
            fontSize: "32px",
            fontWeight: "bold",
          }}
          items={[
            {
              key: "5",
              label: "Instruction",
              // SP3: the SoP sits inline at the end of the instruction it belongs to, rather
              // than taking a row of its own. The chip opens the version frozen on the batch
              // header at issuance, not the newest one.
              children: (
                <span className="inline-flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span>{operation_data?.instruction}</span>
                  <SopChip
                    batchNumber={operation_data?.batch_number}
                    operationId={operation_data?.operation_id}
                  />
                </span>
              ),
              span: 2,
            },
            {
              key: "1",
              label: "Batch No",
              children: operation_data?.batch_number,
            },
            {
              key: "2",
              label: "Operation",
              children: `${operation_data?.operation_type} - ${operation_data?.operation_number}`,
            },
            {
              key: "3",
              label: "Machine",
              children: (
                <Select
                  value={machine}
                  size="large"
                  placeholder="Select Machine"
                  onChange={(value) => set_machine(value)}
                  style={{
                    width: "100%",
                    fontSize: "24px",
                  }}
                  options={machine_data?.map((item) => ({
                    label: item.machine_name,
                    value: item.machine_id,
                  }))}
                />
              ),
            },
            {
              key: "4",
              label: "Duration",
              children: operation_data?.max_duration,
            },

          ]}
        />

        {/* X2: required vs provided attachments for this step; completion is refused while one is missing */}
        <div className="mt-4">
          <AttachmentsPanel
            batchNumber={operation_data?.batch_number}
            operationId={operation_data?.operation_id}
          />
        </div>

        <div className="my-8 space-y-6">
          {operation_data?.paramaters?.map((item, index) => (
            <div key={index} className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-4">
                <label className="text-2xl font-medium text-center block">
                  {item?.parameter_name}
                </label>
              </div>
              <div className="lg:col-span-8">
                <Input
                  type="number"
                  size="large"
                  value={param_value[index]?.value}
                  onChange={(e) => {
                    handle_parameter(item?.parameter_id, e, index);
                  }}
                  suffix={
                    <Avatar
                      shape="square"
                      style={{
                        color: "#000",
                        backgroundColor: "#fff",
                        fontSize: "24px",
                      }}
                    >
                      {`${item?.parameter_name
                        ?.split(" ")
                        .pop()
                        .replace(")", "")}`}
                    </Avatar>
                  }
                  style={{
                    height: "50px",
                    fontSize: "24px",
                  }}
                />
              </div>
            </div>
          ))}

          <div className="flex justify-end items-end gap-2 mt-6">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{
                backgroundColor: defaultColors.primary,
                borderColor: defaultColors.primary,
                height: "50px",
                fontSize: "24px",
              }}
            >
              Submit
            </Button>
          </div>
        </div>
      </form>
      <AcknowledgeHandoverModal
        open={!!handoverOp}
        batchNumber={operation_data?.batch_number}
        operationId={handoverOp}
        onCancel={() => setHandoverOp(null)}
        onAcknowledged={() => {
          setHandoverOp(null);
          submit_function();
        }}
      />
    </MainCard>
  );
}

export default AddData;
